import { useCallback, useEffect, useRef, useState } from 'react';
import { syncSnippetBacklinksForDoc } from '../../../../utils/snippetBacklinks';

const AUTOSAVE_DELAY = 800;

// Run the save flush in an idle slot so serializing/persisting the doc can't
// land exactly when the user resumes typing (timeout still guarantees it)
const whenIdle = (fn) =>
  typeof requestIdleCallback === 'function' ? requestIdleCallback(fn, { timeout: 2000 }) : fn();

/**
 * Debounced autosave with flush-on-unmount. `scheduleSave` batches fields;
 * `saveNow` merges fields and flushes immediately (for already-debounced UIs
 * like the variables panel).
 */
export function useDocAutosave(docId, onSave) {
  const [saveState, setSaveState] = useState('idle'); // idle | dirty | saving | saved | error
  const pendingRef = useRef({});
  const timerRef = useRef(null);
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  const flush = useCallback(async () => {
    clearTimeout(timerRef.current);
    if (Object.keys(pendingRef.current).length === 0) return;
    const payload = pendingRef.current;
    pendingRef.current = {};
    setSaveState('saving');
    try {
      await onSaveRef.current(docId, payload);
      if (payload.body) {
        syncSnippetBacklinksForDoc(docId, payload.body).catch(() => {});
      }
      setSaveState(Object.keys(pendingRef.current).length ? 'dirty' : 'saved');
    } catch {
      pendingRef.current = { ...payload, ...pendingRef.current };
      setSaveState('error');
    }
  }, [docId]);

  const scheduleSave = useCallback(
    (fields) => {
      pendingRef.current = { ...pendingRef.current, ...fields };
      // Functional no-op update when already dirty — avoids an extra host
      // re-render on every keystroke
      setSaveState((s) => (s === 'dirty' ? s : 'dirty'));
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => whenIdle(flush), AUTOSAVE_DELAY);
    },
    [flush]
  );

  const saveNow = useCallback(
    (fields) => {
      pendingRef.current = { ...pendingRef.current, ...fields };
      setSaveState((s) => (s === 'dirty' ? s : 'dirty'));
      flush();
    },
    [flush]
  );

  useEffect(() => {
    return () => {
      clearTimeout(timerRef.current);
      if (Object.keys(pendingRef.current).length > 0) {
        const payload = pendingRef.current;
        pendingRef.current = {};
        Promise.resolve(onSaveRef.current(docId, payload))
          .then(() => {
            if (payload.body) {
              return syncSnippetBacklinksForDoc(docId, payload.body);
            }
            return undefined;
          })
          .catch(() => {});
      }
    };
  }, [docId]);

  return { saveState, scheduleSave, saveNow, flush };
}
